import type { AccountTypeStat } from '@/hooks/useSuperStats';

interface AccountTypeBreakdownProps {
  items: AccountTypeStat[];
  loading: boolean;
  activeType?: string | null;
  onTypeClick?: (key: string) => void;
}

interface TypeMeta {
  icon: string;
  barClass: string;
  chipClass: string;
}

const TYPE_META: Record<string, TypeMeta> = {
  Boutique: {
    icon: 'ri-store-2-line',
    barClass: 'bg-primary-500',
    chipClass: 'bg-primary-100 text-primary-700',
  },
  Services: {
    icon: 'ri-briefcase-4-line',
    barClass: 'bg-secondary-500',
    chipClass: 'bg-secondary-100 text-secondary-700',
  },
  Mixte: {
    icon: 'ri-shuffle-line',
    barClass: 'bg-accent-500',
    chipClass: 'bg-accent-100 text-accent-700',
  },
  Restauration: {
    icon: 'ri-restaurant-2-line',
    barClass: 'bg-foreground-800',
    chipClass: 'bg-background-200/70 text-foreground-700',
  },
  Autres: {
    icon: 'ri-apps-2-line',
    barClass: 'bg-background-300',
    chipClass: 'bg-background-100 text-foreground-600',
  },
};

/**
 * Répartition des sites de la plateforme par type de compte
 * (Boutique / Services / Mixte / Restauration / Autres).
 */
export default function AccountTypeBreakdown({ items, loading, activeType = null, onTypeClick }: AccountTypeBreakdownProps) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const sorted = [...items].sort((a, b) => b.value - a.value);
  const clickable = typeof onTypeClick === 'function';

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 mb-5 md:mb-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
          <i className="ri-pie-chart-2-line text-primary-600"></i>
          Répartition par type de compte
        </h3>
        <span className="text-xs text-foreground-500 whitespace-nowrap">
          {clickable ? 'Cliquez une catégorie pour filtrer' : `${total} site${total > 1 ? 's' : ''} au total`}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-24">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-300"></i>
        </div>
      ) : total === 0 ? (
        <p className="text-sm text-foreground-400 text-center py-6">
          Aucun site enregistré pour le moment.
        </p>
      ) : (
        <>
          {/* Barre empilée globale */}
          <div className="flex w-full h-3 rounded-full overflow-hidden bg-background-100 mb-5">
            {sorted
              .filter((item) => item.value > 0)
              .map((item) => {
                const meta = TYPE_META[item.key] || TYPE_META.Autres;
                const pct = (item.value / total) * 100;
                return (
                  <div
                    key={item.key}
                    className={meta.barClass}
                    style={{ width: `${pct}%` }}
                    title={`${item.label} : ${item.value} (${pct.toFixed(0)}%)`}
                  ></div>
                );
              })}
          </div>

          {/* Légende détaillée */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {sorted.map((item) => {
              const meta = TYPE_META[item.key] || TYPE_META.Autres;
              const pct = total > 0 ? (item.value / total) * 100 : 0;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={clickable ? () => onTypeClick!(item.key) : undefined}
                  className={`rounded-lg border p-3.5 text-left transition-colors ${
                    activeType === item.key
                      ? 'bg-primary-50 border-primary-300 ring-1 ring-primary-300'
                      : clickable
                        ? 'bg-background-100 border-background-200/60 cursor-pointer hover:border-primary-200 hover:bg-primary-50/40'
                        : 'bg-background-100 border-background-200/60'
                  }`}
                >
                  <span
                    className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${meta.chipClass}`}
                  >
                    <i className={meta.icon}></i>
                  </span>
                  <div className="text-xl font-bold font-heading text-foreground-950">
                    {item.value.toLocaleString('fr-FR')}
                  </div>
                  <div className="text-[11px] text-foreground-500 leading-snug mt-0.5 flex items-center justify-between gap-1">
                    <span className="truncate">{item.label}</span>
                    <span className="text-foreground-400">{pct.toFixed(0)}%</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}